import { createRoot } from "react-dom/client";
import { toast } from "sonner";
import App from "./App.tsx";
import ErrorBoundary from "./components/ErrorBoundary.tsx";
import { normalizeLangCode } from "./i18n";
import "./index.css";

// Prevent PWA service worker from interfering in iframe/preview contexts
const isInIframe = (() => {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
})();

const isPreviewHost =
  window.location.hostname.includes("id-preview--") ||
  window.location.hostname.includes("lovableproject.com");

const toastMessages: Record<string, { offline: string; refresh: string }> = {
  ko: { offline: '오프라인 캐시 준비가 완료되었습니다.', refresh: '새 버전을 감지해 지금 새로고침합니다...' },
  en: { offline: 'Offline cache is ready.', refresh: 'New version detected. Refreshing now...' },
  de: { offline: 'Offline-Cache ist bereit.', refresh: 'Neue Version erkannt. Die Seite wird jetzt aktualisiert...' },
  es: { offline: 'La caché sin conexión está lista.', refresh: 'Se detectó una nueva versión. Actualizando ahora...' },
  fr: { offline: 'Le cache hors ligne est prêt.', refresh: 'Nouvelle version détectée. Actualisation en cours...' },
  ar: { offline: 'أصبح التخزين المؤقت دون اتصال جاهزًا.', refresh: 'تم اكتشاف إصدار جديد. جارٍ التحديث الآن...' },
};

const runtimeLang = normalizeLangCode(window.location.pathname.split('/')[1]) ?? 'en';
const runtimeToast = toastMessages[runtimeLang] ?? toastMessages.en;

const startNonCriticalClients = async () => {
  const [{ registerSW }, { initRumVitals }] = await Promise.all([
    import('virtual:pwa-register'),
    import('./lib/rumVitals'),
  ]);

  const updateSW = registerSW({
    immediate: true,
    onOfflineReady() {
      toast.success(runtimeToast.offline);
    },
    onNeedRefresh() {
      toast.info(runtimeToast.refresh);
      setTimeout(() => {
        updateSW(true);
      }, 900);
    },
  });

  void initRumVitals();
};

if (isPreviewHost || isInIframe) {
  navigator.serviceWorker?.getRegistrations().then((registrations) => {
    registrations.forEach((r) => r.unregister());
  });
} else if ("requestIdleCallback" in window) {
  window.addEventListener("load", () => {
    (window as Window & {
      requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => unknown;
    }).requestIdleCallback?.(startNonCriticalClients, { timeout: 2000 });
  });
} else {
  (window as Window).addEventListener("load", () => {
    (window as Window).setTimeout(startNonCriticalClients, 1200);
  });
}

createRoot(document.getElementById("root")!).render(
  <ErrorBoundary>
    <App />
  </ErrorBoundary>
);
